import { AfterContentChecked, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Output, AfterViewInit, OnDestroy, OnInit, ChangeDetectorRef, HostBinding, Injector, ContentChildren, QueryList, AfterContentInit, Input, Optional } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { NavController } from 'providers/nav-controller';
import { waitForAngularUpdate } from 'services/browser-utils';
import { ConnectionStateService, DebouncedCloudStateChangeEvent } from 'services/connection-state.service';
import { DatabaseState } from 'services/database-wrapper';
import { PubSubService, navigationStackChangedEvent, PubSubRegistration } from 'services/pubsub.service';
import { ScrollHideService, ScrollHideRegistration } from 'services/scroll-hide.service';

// button flags for header action buttons (multiple can be set via bitwise OR)
export enum CsHeaderButtons {
  Back = 1 << 0,
  Accept = 1 << 1,
  Cancel = 1 << 2,
}

@Component({
    selector: 'cs-header',
    templateUrl: 'cs-header.html',
  host: { 'class': 'header' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsHeaderComponent implements OnInit, AfterContentInit, AfterContentChecked, AfterViewInit, OnDestroy {
  protected LL = LL;
  protected BadgeColor = BadgeColor;
  @HostBinding('style.transform') get transform(): string {
    return `translateY(${this.translateY}px)`;
  }

  @HostBinding('class.disable-hide-on-scroll') get disableHideOnScrollClass(): boolean {
    return this.disableHideOnScroll;
  }

  @HostBinding('class.full-width') get fullWidthClass(): boolean {
    return this.fullWidth;
  }

  @Input() disableHideOnScroll = false;
  @Input() backButton: 'auto' | boolean = 'auto';
  /** F8 (#3991): the 2px syncing/offline line (cs-header-progress). On by default — it is a thin
   *  ambient hint that costs no layout space. Set false on a cs-header that is not a page header:
   *  the side menu has its own cs-header for the logo lockup, and a second line there is just
   *  noise. */
  @Input() showConnectionStatus = true;

  /** F8 (#3991): the standing "Offline" pill. OFF by default — opt in per page.
   *
   *  Only Home opts in. The pill is a word plus an icon, and on a phone a busy header (back arrow +
   *  title + actions + overflow) has no room for it — it pushed the page title into an ellipsis
   *  ("User Pr…"). Home is the screen the user lands on and lingers on, its header is uncluttered,
   *  and it is where a standing "you are offline" belongs. Every other page still shows the 2px
   *  line, exactly as before this ticket. (Owner decision, 2026-07-13.) */
  @Input() showOfflineBadge = false;
  @Input() fullWidth = false;
  @Input() hideType: 'default' | 'auto' = 'default';
  @Input() separator = false;
  @Output() click = new EventEmitter<Event>();
  @ContentChildren('[header-right]', { descendants: true, read: ElementRef }) headerRightContent: QueryList<ElementRef>;
  @ContentChildren('[header-left]', { descendants: true, read: ElementRef }) headerLeftContent: QueryList<ElementRef>;

  // scroll detection properties
  private translateY = 0;
  private headerHeight = 0;
  private headerResizeObserver: ResizeObserver;
  private navigationSubscription: PubSubRegistration;
  private headerRightElement: HTMLElement;
  private headerRightObserver: MutationObserver;
  private headerLeftElement: HTMLElement;
  private headerLeftObserver: MutationObserver;
  private scrollHideRegistration: ScrollHideRegistration;

  // F8 (#3991): the standing "Offline" pill. The 2px offline line and the one-off toast stay
  // as they are — the toast announces the transition, the pill shows the standing state. We read
  // the DEBOUNCED cloud state, so a brief sync hiccup does not flap the pill (5 s debounce,
  // suppressed while backgrounded — both inherited from ConnectionStateService on purpose).
  private cloudSyncState: DatabaseState = undefined;
  private cloudStateSub: PubSubRegistration;
  /** Set in ngOnDestroy so the pubsub callback cannot markForCheck a destroyed view (NG0911). */
  private viewDestroyed = false;

  hasHeaderRightContent = false;
  hasHeaderLeftContent = false;
  private isModalPage = false;
  private _lastBackButtonState = false;

  constructor(
    private navCtrl: NavController,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private pubSub: PubSubService,
    private scrollHideService: ScrollHideService,
    private connectionStateService: ConnectionStateService,
    @Optional() private modalMarker: ModalPageMarker
  ) {
    // read this here rather than in ngAfterViewInit: `dismissIcon` needs it on the FIRST
    // render, or a desktop modal draws a back arrow and then swaps it for an X
    this.isModalPage = this.modalMarker != null;
  }

  ngOnInit() {
    // seed from the service, then follow it — same pattern as cs-header-progress
    this.cloudSyncState = this.connectionStateService.debouncedState;
    this.cloudStateSub = this.pubSub.subscribe(DebouncedCloudStateChangeEvent, (data: { state: DatabaseState }) => {
      // guard per CLAUDE.md's NG0911 rule. The ngOnDestroy unsubscribe already makes this safe in
      // practice, but a markForCheck on a destroyed view is the exact shape of that crash, and a
      // future refactor that moves the unsubscribe would reintroduce it silently.
      if (this.viewDestroyed) return;
      this.cloudSyncState = data.state;
      this.cd.markForCheck();
    });
  }

  /** True while the app is offline AND this header opted into the pill (Home only). */
  get isOffline(): boolean {
    return this.showOfflineBadge && this.cloudSyncState === DatabaseState.Offline;
  }

  /**
   * Whether to show the one button that dismisses this page. It always sits at the LEFT of the
   * header, whether it takes the user back a page or closes a dialog, so that "get me out of
   * here" is in the same place on every screen. `dismissIcon` picks which of the two it looks
   * like. (Owner decision, 2026-08-27: modal pages used to put a close X at the RIGHT instead,
   * which on a phone read as a dialog control on what is really a plain full-screen page.)
   */
  get showDismissButton(): boolean {
    if (this.backButton === 'auto') {
      // if page containing this header is not the active page (e.g., modal on top),
      // preserve the last computed state - don't re-evaluate canGoBack for inactive pages
      if (!this.isContainingPageActive()) {
        return this._lastBackButtonState;
      }
      this._lastBackButtonState = this.navCtrl.canGoBack();
      return this._lastBackButtonState;
    }
    return this.backButton;
  }

  /**
   * Check if the page containing this header is the active (topmost) page.
   * When a modal is on top, pages below it should not re-evaluate their back button state.
   *
   * Uses NavController.getActive() instead of DOM classes because the navigation stack
   * is updated before DOM classes during page transitions (cs-nav.ts line 302 vs 408).
   */
  private isContainingPageActive(): boolean {
    // find the cs-page element that contains this header
    const pageElement = this.elementRef.nativeElement.closest('.cs-page');
    if (!pageElement) return true; // fallback to true if not found

    // get the topmost page from NavController
    const topPage = this.navCtrl.getActive();
    if (!topPage) return true;

    // compare DOM elements - our containing page vs the active page
    const topPageElement = topPage.component?.location?.nativeElement;
    return pageElement === topPageElement;
  }

  /**
   * True when this page is drawn as a floating card in the middle of the screen rather than
   * filling it: a modal page on a wide screen, or one that asked not to be full-screen on a
   * phone (`fullScreenOnMobile: false`). This mirrors `modalUsesDesktopBehavior` in cs-nav.ts,
   * which is what actually decides the presentation — keep the two in step.
   */
  private get isFloatingCard(): boolean {
    if (!this.isModalPage) {
      return false;
    }
    return !hasClientSmallScreen() || this.modalMarker?.config?.fullScreenOnMobile === false;
  }

  /**
   * Which glyph the dismiss button wears. A page drawn as a floating card gets a close X,
   * because that is what closes a dialog; anything drawn full-screen gets the ordinary back
   * arrow, because it is an ordinary page and leaving it goes back.
   */
  get dismissIcon(): 'close' | 'arrow-back' {
    return this.isFloatingCard ? 'close' : 'arrow-back';
  }

  ngAfterContentInit() {
    // subscribe to changes in content projection
    this.headerRightContent.changes.subscribe(() => {
      this.updateHeaderRightVisibility();
    });
    this.headerLeftContent.changes.subscribe(() => {
      this.updateHeaderLeftVisibility();
    });
    // initial check
    this.updateHeaderRightVisibility();
    this.updateHeaderLeftVisibility();
  }

  ngAfterContentChecked() {
    // trigger change detection if back button visibility changed
    // update header-right and header-left visibility check
    this.updateHeaderRightVisibility();
    this.updateHeaderLeftVisibility();
    this.cd.markForCheck();
  }

  private updateHeaderRightVisibility() {
    // check for projected content via ng-content
    const hasProjectedContent = this.headerRightContent && this.headerRightContent.length > 0;

    // check for dynamically added content in the header-right element
    let hasDynamicContent = false;
    if (this.headerRightElement) {
      // check if there are any child elements (not just text nodes)
      hasDynamicContent = this.headerRightElement.children.length > 0;
    }

    // the dismiss button now lives in header-left, so nothing this header owns itself
    // counts towards header-right being occupied
    const hasContent = hasProjectedContent || hasDynamicContent;
    if (this.hasHeaderRightContent !== hasContent) {
      this.hasHeaderRightContent = hasContent;
      this.cd.markForCheck();
    }
  }

  private updateHeaderLeftVisibility() {
    // check for projected content via ng-content
    const hasProjectedContent = this.headerLeftContent && this.headerLeftContent.length > 0;

    // check for dynamically added content in the header-left element
    let hasDynamicContent = false;
    if (this.headerLeftElement) {
      // check if there are any child elements (not just text nodes)
      hasDynamicContent = this.headerLeftElement.children.length > 0;
    }

    const hasContent = hasProjectedContent || hasDynamicContent;
    if (this.hasHeaderLeftContent !== hasContent) {
      this.hasHeaderLeftContent = hasContent;
      this.cd.markForCheck();
    }
  }

  async ngAfterViewInit() {
    await waitForAngularUpdate(this.injector);
    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so everything below has to be skipped in that case. It all
    // REGISTERS something ngOnDestroy is supposed to take away again, and ngOnDestroy
    // has already run by then — and two of those registries, ScrollHideService and
    // PubSubService, live as long as the app, so a late registration holds this dead
    // component and its host element for the rest of the session
    if (this.viewDestroyed) return;

    // set initial height
    this.headerHeight = this.elementRef.nativeElement.offsetHeight;

    // setup ResizeObserver to track header height changes
    this.headerResizeObserver = new ResizeObserver(() => {
      const newHeight = this.elementRef.nativeElement.offsetHeight;
      if (this.headerHeight !== newHeight) {
        this.headerHeight = newHeight;
        this.cd.markForCheck();
      }
    });
    this.headerResizeObserver.observe(this.elementRef.nativeElement);

    // register with ScrollHideService for scroll-based hiding
    this.scrollHideRegistration = this.scrollHideService.register({
      element: this.elementRef.nativeElement,
      direction: 'up', // header hides by translating up
      onScrollChange: (translateY) => {
        this.translateY = translateY;
        this.cd.markForCheck();
      },
      enabled: () => !this.disableHideOnScroll && !(this.isModalPage && !hasClientSmallScreen()),
      hideType: this.hideType
    });

    // Subscribe to navigation stack changes to update back button visibility
    this.navigationSubscription = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      this.cd.markForCheck();
    });

    // Setup MutationObserver for header-right to detect dynamic content changes
    this.headerRightElement = this.elementRef.nativeElement.querySelector('.header-right');
    if (this.headerRightElement) {
      this.headerRightObserver = new MutationObserver(() => {
        this.updateHeaderRightVisibility();
      });

      // observe for child list changes (elements being added/removed)
      this.headerRightObserver.observe(this.headerRightElement, {
        childList: true,
        subtree: true
      });

      // initial check for dynamic content
      this.updateHeaderRightVisibility();
    }

    // Setup MutationObserver for header-left to detect dynamic content changes
    this.headerLeftElement = this.elementRef.nativeElement.querySelector('.header-left');
    if (this.headerLeftElement) {
      this.headerLeftObserver = new MutationObserver(() => {
        this.updateHeaderLeftVisibility();
      });

      // observe for child list changes (elements being added/removed)
      this.headerLeftObserver.observe(this.headerLeftElement, {
        childList: true,
        subtree: true
      });

      // initial check for dynamic content
      this.updateHeaderLeftVisibility();
    }
  }

  ngOnDestroy() {
    this.viewDestroyed = true;
    if (this.cloudStateSub) {
      this.cloudStateSub.unsubscribe();
    }
    if (this.scrollHideRegistration) {
      this.scrollHideRegistration.unregister();
    }
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    if (this.headerRightObserver) {
      this.headerRightObserver.disconnect();
    }
    if (this.headerLeftObserver) {
      this.headerLeftObserver.disconnect();
    }
  }

  handleBackButtonClick(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }
}